import findIndex from 'lodash/findIndex';
import {VNode} from 'snabbdom/vnode';
import {AttrsSchema, Component} from 'panel';

import {useEventListener} from 'common/util/component-helpers';
import {truncateMiddle} from 'common/util/string';
import {getModifierKeys, SHIFT_KEY_CODE} from 'common/util/dom';
import {stickyElemHooks} from 'common/util/dom-sticky-stack';
import {fuzzyMatchSanitized, matchPartsFromSegments} from 'common/util/fuzzy-match';
import {defineAndMakeJsxFactory} from 'common/util/jsx';

import {TrackingEvent} from './tracking';
import {template} from './template';
import css from './index.styl?inline';
import {
  CellClickEvent,
  CellRenderContext,
  ColumnMovedEvent,
  DisplayTableRow,
  NumResultsChangeEvent,
  RowClickEvent,
  RowDetailsVisibilityChangeEvent,
  SelectionChangeEvent,
  SortChangeEvent,
  TableColumn,
  TableRow,
  TableScrollEvent,
  TableState,
} from './types';

const COMPONENT_NAME = `mp-table`;
const PROGRESSIVE_SIZE_ROWS = 25;
// TODO: Dynamically calculate based on available height
const INITIAL_NUM_DISPLAY_ROWS = PROGRESSIVE_SIZE_ROWS * 2;
const PROGRESSIVE_HEIGHT_BUFFER_PX = 1750;
const COLUMN_TITLE_MAX_LENGTH = 20;

enum DROP_POSITION {
  LEFT,
  RIGHT,
}

interface TableJsxProps {
  attrs?: {
    loading?: boolean;
    'multi-select'?: boolean;
    'selection-limit'?: number;
    'empty-message'?: string;
    'search-filter'?: string;
    'default-sort-field'?: string;
    'default-sort-order'?: number;
    'max-body-height'?: string;
    'num-sticky-rows'?: number;
    'sticky-titles'?: boolean;
    'disable-page-sticky-titles'?: boolean;
    'disable-clickable-rows'?: boolean;
    'disable-expandable-rows'?: boolean;
    'min-row-count'?: number;
    'row-focused-context'?: boolean;
    'track-name'?: string;
    'window-scroll'?: boolean;
    'draggable-columns'?: boolean;
    'draggable-after-index'?: number;
    'hide-titles'?: boolean;
    'disable-auto-sorting'?: boolean;
  };
  props?: {
    columns?: TableColumn[];
    rows?: TableRow[];
    rowDetailsRenderer?: (TableRow) => VNode;
  };
  on?: {
    selectionChange?: (ev: SelectionChangeEvent) => void;
    rowClick?: (ev: RowClickEvent<TableRow>) => void;
    cellClick?: (ev: CellClickEvent<TableRow>) => void;
    sortChange?: (ev: SortChangeEvent<TableRow>) => void;
    numResultsChange?: (ev: NumResultsChangeEvent) => void;
    rowDetailsVisibilityChange?: (ev: RowDetailsVisibilityChangeEvent) => void;
    columnMoved?: (ev: ColumnMovedEvent) => void;
    tableScroll?: (ev: TableScrollEvent) => void;
  };
}

export class Table extends Component<TableState, TableJsxProps['attrs']> {
  static get attrsSchema(): AttrsSchema<TableJsxProps['attrs']> {
    return {
      ...super.attrsSchema,
      loading: {
        type: `boolean`,
      },
      'multi-select': {
        type: `boolean`,
        description: `Show checkboxes besides each row and allow select all / none of current filtered results`,
      },
      'selection-limit': {
        type: `number`,
        description: `Limit the number of selections allowed. Also disables shift-click.`,
      },
      'empty-message': {
        type: `string`,
        default: `This table is empty. Let's make a fresh start`,
      },
      'search-filter': {
        type: `string`,
        description: `Only display rows that match the search-filter and highlight the matches`,
      },
      'default-sort-field': {
        type: `string`,
        description: `Field by which table should be initially sorted. Updating this attribute will reset the sort to default-sort-field.`,
      },
      'default-sort-order': {
        type: `number`,
        default: 1,
        description: `Initial sort order, 1 for ascending, -1 for descending`,
      },
      'disable-expandable-rows': {
        type: `boolean`,
        description: `Whether to disable the expandable rows despite the presence of a renderer for it`,
      },
      'max-body-height': {
        type: `string`,
        description: `Max body height with units, more rows are loaded as user scrolls progressively`,
      },
      'num-sticky-rows': {
        type: `number`,
        default: 0,
        description: `Sticky rows that will not be sorted, that are to be placed at the start of the table. These rows are to be indexed by [0, 1, ..., num-sticky-rows) in the table rows passed in by the user. By default, all rows are sortable.`,
      },
      'sticky-titles': {
        type: `boolean`,
        description: `apply sticky positioning to the component and to the whole page`,
      },
      'disable-page-sticky-titles': {
        type: `boolean`,
        description: `apply sticky positioning to the component, but not to the whole page`,
      },
      'disable-clickable-rows': {
        type: `boolean`,
        description: `Rows do not show cursor pointer, change bg color on hover or fire clickRow events`,
      },
      'min-row-count': {
        type: `number`,
        default: 0,
        description: `The minimum number of rows to be displayed. If under, blank rows will be appended to the table`,
      },
      'row-focused-context': {
        type: `boolean`,
        description: `Cell renderers are given isRowFocused as render context.
          Turning this on makes an update per row hover.
          Use it to conditionally render perf intensive components`,
      },
      'track-name': {
        type: `string`,
        description: `If specified, dispatches '[mp-table] <event>' bubbled events that can be listened for tracking with name as a property`,
      },
      'window-scroll': {
        type: `boolean`,
        description: `Assign progressive scroll handler to window instead of table body`,
      },
      'draggable-columns': {
        type: `boolean`,
        description: `True if it allows re-order columns with drag and drop`,
      },
      'draggable-after-index': {
        type: `number`,
        description: `Prevents dragging and dropping for columns less than the given index`,
      },
      'hide-titles': {
        type: `boolean`,
        description: `Do not show titles for the table`,
      },
      'disable-auto-sorting': {
        type: `boolean`,
        description: `If it is true, mp-table won't re-order the rows when the user clicks the sort controllers, but it still triggers the sort events`,
      },
    };
  }

  private elRefs: {[key: string]: HTMLElement};
  private displayRowsCache: void | DisplayTableRow[];
  private isMouseOver: boolean;
  private loadingStartTimeMs: number;
  private focusedRowIdx: number;
  private lastSelectedRowIdx: number;
  private numResults: number;

  constructor() {
    super();
    this.loadingStartTimeMs = Date.now(); // unix milliseconds when loading started
    this.elRefs = {}; // named dom element references
    this.displayRowsCache = null; // cache of filtered + sorted rows, so scrolling is super smooth
    this.focusedRowIdx = null;
    this.isMouseOver = false; // so we can only listen for shift up/down when mouse is over the table
    this.lastSelectedRowIdx = null; // for shift+click range select
    this.numResults = -1; // so we can fire numResultsChange event
  }

  get config() {
    return {
      template,
      css,
      useShadowDom: true,
      defaultState: {
        rows: [],
        rowDetailsRenderer: null,
        columns: [],
        doingRangeSelect: false,
        draggingColumnIndex: null,
        draggingOverColumnIndex: null,
        dropPosition: null,
        focusedRowIdx: null,
        hasBodyScrolled: false,
        numRowsToDisplay: INITIAL_NUM_DISPLAY_ROWS,
        sortField: null,
        sortOrder: 1,
      },
      helpers: {
        COLUMN_TITLE_MAX_LENGTH,
        stickyElemHooks,
        truncateMiddle,
        getRowsToDisplay: (): DisplayTableRow[] => this._getDisplayRows().slice(0, this.state.numRowsToDisplay),

        getCellLabel: (row: TableRow, col: TableColumn): string => this._getCellLabel(row, col),

        getColumnType: (row: TableRow, col: TableColumn) =>
          col.type || (col.typeTransform && col.typeTransform(row, col.field)) || (col.cellRenderer ? `` : `text`),

        getCellRenderContexts: (
          cols: TableColumn[],
          row: DisplayTableRow,
          rowIdx: number,
          totalRows: number,
        ): CellRenderContext[] => {
          return cols.map((col, colIdx) => this.helpers.getCellRenderContext(row, col, colIdx, rowIdx, totalRows));
        },

        getCellRenderContext: (
          row: DisplayTableRow,
          col: TableColumn,
          columnIdx: number,
          rowIdx: number,
          totalRows: number,
        ): CellRenderContext => ({
          field: col.field,
          isRowFocused: this.state.focusedRowIdx === rowIdx,
          label: this.helpers.getCellLabel(row.val, col),
          matchParts: row.matchParts && row.matchParts[col.field],
          totalRows,
          columnIdx,
          rowIdx,
        }),

        getColumnsToDisplay: (): TableColumn[] => {
          // set rowDetaisRenderer will add a special collapsible caret column on the left with isExpanded as the field
          const collapsibleColumn =
            this.state.rowDetailsRenderer && !this.attr(`disable-expandable-rows`)
              ? [{field: `isExpanded`, type: `collapsible`}]
              : [];
          // multi-select adds a special checkbox column on the left with isSelected as the field
          const checkboxColumn = this.attr(`multi-select`) ? [{field: `isSelected`, type: `checkbox`}] : [];
          const columns = this.state.columns;

          return [...collapsibleColumn, ...checkboxColumn, ...columns];
        },

        getGroupedColIndices: (
          cols: TableColumn[],
          prevContexts: CellRenderContext[],
          currContexts: CellRenderContext[],
        ): Set<number> => {
          // group-by logic
          //    COL1  COL2  COL3   -> COL1  COL2  COL3
          //      A|    B|    C|   ->   A|    B|    C͟|
          //      A|    B|    D|   ->    |    _|    D͟|
          //      A|    D|    D|   ->   _     D͟|    D͟|
          //      B|    D|    D|   ->   B     D͟|    D͟|
          const groupedColIndices = new Set<number>();
          if (prevContexts === null) {
            return groupedColIndices;
          }
          let addIfNextGroupBy = null;
          const rows = this.getDisplayedRows();
          const getRowValue = (context) => rows[context.rowIdx][context.field];
          for (let colIdx = 0; colIdx < cols.length; ++colIdx) {
            const col = cols[colIdx];
            // For checkboxes it will by grouped if it is behind a grouped value
            if (col.type === `checkbox`) {
              addIfNextGroupBy = colIdx;
              continue;
            }
            if (col.isGroupable && getRowValue(prevContexts[colIdx]) === getRowValue(currContexts[colIdx])) {
              if (addIfNextGroupBy !== null) {
                groupedColIndices.add(addIfNextGroupBy);
              }
              groupedColIndices.add(colIdx);
            } else {
              break;
            }
          }
          return groupedColIndices;
        },
        toggleSelected: (ev, row, rowIdx) => {
          ev.stopPropagation(); // so we don't also fire the rowClick event
          if (document.getSelection) {
            document.getSelection().empty(); // so text isn't highlighted on shift click or double click
          }

          if (!row.isSelectDisabled) {
            const isSelected = !row.isSelected;
            let changed = [{isSelected, row}];

            if (this.helpers.rangeSelectAllowed() && ev.shiftKey && this.lastSelectedRowIdx !== null) {
              // shift+click multi-select behaviour is like gmail
              // change everything in between current and last clicked row to new current state
              const [sliceStartIdx, sliceEndIdx] = [rowIdx, this.lastSelectedRowIdx].sort((a, b) => a - b);
              changed = this.getDisplayedRows()
                .slice(sliceStartIdx, sliceEndIdx + 1) // endIdx + 1 because indices are inclusive
                .filter((r) => !r.isSelectDisabled && r.isSelected !== isSelected)
                .map((r) => ({isSelected, row: r}));
            }

            this.dispatchEvent(
              new CustomEvent(`selectionChange`, {
                detail: {
                  changed,
                  isToggleAll: false,
                  rows: this.getDisplayedRows(),
                },
              }) as SelectionChangeEvent,
            );
            this.lastSelectedRowIdx = rowIdx;
            this.helpers.track(TrackingEvent.SelectionChange, {isSelected, numChanged: changed.length});
          }
        },

        isColumnDraggable: (colIndex) => {
          const draggableIndex = this.attr(`draggable-after-index`);
          return !draggableIndex || colIndex > draggableIndex;
        },

        getSelectionStats: () => {
          const selectableRows = this._getSelectableDisplayedRows();
          const selectedRows = selectableRows.filter((row) => row.isSelected);
          const numSelectable = selectableRows.length;
          const numSelected = selectedRows.length;
          const limit = this.attr(`selection-limit`) || Number.POSITIVE_INFINITY;
          return {
            numSelectable,
            numSelected,
            allSelected: numSelectable > 0 && numSelected === numSelectable,
            atSelectionLimit: numSelected >= limit,
          };
        },

        getCheckboxState: (item: TableRow) => {
          if (item.isSelected) {
            const options: any = {
              attrs: {
                checked: true,
                disabled: false,
              },
            };
            if (item.checkedColor) {
              options.attrs[`checked-color`] = item.checkedColor;
            }
            if (item.checkedIcon) {
              options.attrs[`checked-icon`] = item.checkedIcon;
            }
            return options;
          }

          const {atSelectionLimit} = this.helpers.getSelectionStats();
          if (atSelectionLimit) {
            return {
              selectDisabledReason:
                item.selectDisabledReason || `You can't select more than ${this.attr(`selection-limit`)} items.`,
              attrs: {disabled: true, checked: false},
            };
          }

          return {
            selectDisabledReason: item.selectDisabledReason,
            attrs: {disabled: item.isSelectDisabled, checked: false},
          };
        },

        toggleAllSelected: () => {
          // partial -> none, none -> all, all -> none (like gmail)
          const isSelected = this.helpers.getSelectionStats().numSelected === 0;
          const changed = this._getSelectableDisplayedRows()
            .filter((row) => row.isSelected !== isSelected)
            .map((row) => ({isSelected, row}));
          this.dispatchEvent(
            new CustomEvent(`selectionChange`, {
              detail: {
                changed,
                isToggleAll: true,
                rows: this.getDisplayedRows(),
              },
            }) as SelectionChangeEvent,
          );
          this.helpers.track(TrackingEvent.SelectionChange, {isSelected, numChanged: changed.length, toggleAll: true});
        },

        handleCollapsibleCaretClick: (ev, row) => {
          this.dispatchEvent(
            new CustomEvent(`rowDetailsVisibilityChange`, {
              detail: {
                changed: [{isExpanded: !row.isExpanded, row}],
              },
            }) as RowDetailsVisibilityChangeEvent,
          );
          ev.stopPropagation();
        },

        handleCollapseAll: () => {
          this.dispatchEvent(
            new CustomEvent(`rowDetailsVisibilityChange`, {
              detail: {
                changed: this.state.rows.map((row) => ({isExpanded: false, row})),
              },
            }) as RowDetailsVisibilityChangeEvent,
          );
        },

        // NOTE: currently, range selection isn't allowed if there is a selection-limit to keep things simple.
        // restriction may be removed in the future if there is popular demand
        rangeSelectAllowed: () => !this.attr(`selection-limit`),

        willRangeSelect: (rowIdx) => {
          // provide UI feedback on what will be selected
          if (this.state.doingRangeSelect) {
            const [startIdx, endIdx] = [this.focusedRowIdx, this.lastSelectedRowIdx].sort((a, b) => a - b);
            return rowIdx >= startIdx && rowIdx <= endIdx;
          }
          return false;
        },

        handleHeaderTitleClick: (column: TableColumn) => {
          const state = this.state;

          if (column.isSortable) {
            const sortField = column.field;
            // if same column is clicked, toggle sort order, otherwise keep it same
            const sortOrder = (state.sortField === sortField ? -1 : 1) * state.sortOrder;
            const sortDetail = {sortField, sortOrder};
            this.update(sortDetail);
            this.resetScroll();

            this._emitSortChange(true);
            this.helpers.track(TrackingEvent.SortChange, {field: sortField, order: sortOrder});
          }
        },

        handleRowClick: (ev, row, rowIdx) => {
          if (!this.attr(`disable-clickable-rows`)) {
            this.dispatchEvent(
              new CustomEvent(`rowClick`, {
                detail: {
                  row,
                  rowIdx,
                  target: ev.target,
                  modifierKeys: getModifierKeys(ev),
                },
              }) as RowClickEvent,
            );
          }
        },

        handleCellClick: (ev, row, field, context) => {
          this.dispatchEvent(
            new CustomEvent(`cellClick`, {
              detail: {
                row,
                field,
                context,
                target: ev.target,
                modifierKeys: getModifierKeys(ev),
              },
            }) as CellClickEvent,
          );
          this.helpers.track(TrackingEvent.CellClick, {field, searchFilterLen: this.attr(`search-filter`).length});
        },

        handleBodyMouseEnter: () => {
          this.isMouseOver = true;
        },

        handleBodyMouseLeave: () => {
          this.focusedRowIdx = null;
          this.isMouseOver = false;
          if (this.attr(`row-focused-context`)) {
            this.update({focusedRowIdx: this.focusedRowIdx});
          }
        },

        handleRowMouseEnter: (rowIdx: number) => {
          this.focusedRowIdx = rowIdx;
          // row-focused-context is opt-in because default row hover is done on css which is much more efficient
          // only tables that require context.focusedRowIdx to render should cause re-render per row mouse enter
          if (this.attr(`row-focused-context`)) {
            this.update({focusedRowIdx: this.focusedRowIdx});
            // eslint-disable-next-line sonarjs/no-duplicated-branches
          } else if (this.attr(`multi-select`) && this.state.doingRangeSelect) {
            // shift+click UX feedback for multi-select table requires us to re-render when row focus changes
            // we only update if user is performing a range select, otherwise it will unnecessarily slow down the table
            this.update({focusedRowIdx: this.focusedRowIdx});
          }
        },

        handleScroll: ({target}) => {
          const state = this.state;
          const windowScroll = this.attr(`window-scroll`);
          const scrollTop = windowScroll ? window.scrollY : target.scrollTop;
          const scrollBottom = scrollTop + (windowScroll ? window.innerHeight : target.offsetHeight);
          const scrollHeight = windowScroll ? document.body.clientHeight : target.scrollHeight;
          const hasBodyScrolled = scrollTop > 0 && !windowScroll; // TODO maybe add shadow for window scroll

          if (scrollBottom + PROGRESSIVE_HEIGHT_BUFFER_PX >= scrollHeight) {
            const numRowsToDisplay = Math.min(state.numRowsToDisplay, state.rows.length) + PROGRESSIVE_SIZE_ROWS;
            if (numRowsToDisplay !== state.numRowsToDisplay) {
              this.update({numRowsToDisplay});
              this.helpers.track(TrackingEvent.ProgressiveScroll, {
                numRowsToDisplay,
                numRows: this.state.rows.length,
              });
            }
          }
          if (hasBodyScrolled !== state.hasBodyScrolled) {
            this.update({hasBodyScrolled});
          }
        },

        handleTableScroll: (ev) => {
          this.dispatchEvent(
            new CustomEvent(`tableScroll`, {
              detail: {
                scrollLeft: ev.target.scrollLeft,
              },
            }),
          );
        },

        handleTitleDragStart: (ev, fromIndex) => {
          if (this.attr(`draggable-columns`)) {
            const draggableIndex = this._getDraggableAfterIndex();
            if (fromIndex <= draggableIndex) {
              ev.preventDefault();
              return;
            }

            const tableTitleCell = ev.currentTarget.closest(`.mp-table-title-cell`);
            ev.dataTransfer.setDragImage(tableTitleCell, 0, 0);
            ev.dataTransfer.setData(`text/plain`, fromIndex.toString());
            ev.dataTransfer.effectAllowed = `move`;
            ev.currentTarget.classList.add(`dragging`);
          }
        },

        handleTitleDragEnter: (index) => {
          if (this.attr(`draggable-columns`)) {
            this.update({draggingOverColumnIndex: index});
          }
        },

        handleTitleDragOver: (ev, index) => {
          if (this.attr(`draggable-columns`)) {
            const draggableIndex = this._getDraggableAfterIndex();
            if (index < draggableIndex) {
              return;
            }
            ev.preventDefault();
            ev.dataTransfer.dropEffect = `move`;
            this.update({draggingOverColumnIndex: index, dropPosition: this.getDropPosition(ev)});
          }
        },

        handleTitleStepDragLeave: () => {
          if (this.attr(`draggable-columns`)) {
            this.update({draggingOverColumnIndex: null, dropPosition: null});
          }
        },

        handleTitleStepDragEnd: () => {
          if (this.attr(`draggable-columns`)) {
            this.update({draggingOverColumnIndex: null, dropPosition: null, draggingColumnIndex: null});
          }
        },

        handleTitleDrop: (ev, dropIndex: number) => {
          if (this.attr(`draggable-columns`)) {
            const draggableIndex = this._getDraggableAfterIndex();
            if (dropIndex < draggableIndex) {
              return;
            }

            ev.preventDefault();
            ev.stopPropagation();
            let fromIndex = parseInt(ev.dataTransfer.getData(`text/plain`), 10);
            let toIndex = dropIndex;
            if (this.attr(`multi-select`)) {
              fromIndex--;
              toIndex--;
            }

            // Insert after the drop target index if dropping below
            if (this.getDropPosition(ev) === DROP_POSITION.LEFT) {
              toIndex--;
            }
            this.helpers.handleTitleStepDragEnd();
            const column = this.state.columns[fromIndex];
            this._moveColumnAfter(fromIndex, toIndex);
            this.dispatchEvent(
              new CustomEvent(`columnMoved`, {
                detail: {
                  fromIndex,
                  toIndex,
                  column,
                  columns: this.state.columns,
                },
              }),
            );
            ev.currentTarget.classList.remove(`dragging`);
          }
        },

        handleTitleMouseDown: (_, fromIndex) => {
          if (this.attr(`draggable-columns`)) {
            this.update({draggingColumnIndex: fromIndex});
          }
        },

        handleTitleMouseUp: () => {
          if (this.attr(`draggable-columns`)) {
            this.update({draggingColumnIndex: null});
          }
        },

        isDroppingLeft: (index) =>
          this.state.draggingOverColumnIndex === index && this.state.dropPosition === DROP_POSITION.LEFT,

        isDroppingRight: (index) =>
          this.state.draggingOverColumnIndex === index && this.state.dropPosition === DROP_POSITION.RIGHT,

        isGroupingEnabled: () => this.state.columns.some((col) => col.isGroupable),

        shiftKeyListener: (ev) => {
          if (!this.helpers.rangeSelectAllowed()) {
            return;
          }

          // show shift+click range select feedback UX
          if (ev.keyCode === SHIFT_KEY_CODE) {
            const doingRangeSelect = ev.type === `keydown` && this.isMouseOver && this.lastSelectedRowIdx !== null;
            if (this.state.doingRangeSelect !== doingRangeSelect) {
              this.update({doingRangeSelect});
            }
          }
        },

        updateElRef: (elRef) => {
          Object.assign(this.elRefs, elRef);
        },

        track: (event, props) => {
          if (this.attr(`track-name`)) {
            this.dispatchEvent(
              new CustomEvent(`track`, {
                bubbles: true,
                composed: true,
                cancelable: true,
                detail: {
                  eventName: `[${COMPONENT_NAME}] ${event}`,
                  props: {
                    name: this.attr(`track-name`),
                    ...props,
                  },
                },
              }),
            );
          }
        },
        hasSlot: (slotName: string): boolean => {
          return !!this.querySelector(`[slot="${slotName}"]`);
        },

        trackLoaded: () => {
          this.helpers.track(TrackingEvent.Loaded, {
            durationMs: Date.now() - this.loadingStartTimeMs,
            numRows: this.state.rows && this.state.rows.length,
          });
        },
      },
    };
  }

  public connectedCallback() {
    super.connectedCallback();

    if (this.attr(`multi-select`)) {
      // to show shift+click range select feedback
      useEventListener(this, document.body, `keydown`, this.helpers.shiftKeyListener);
      useEventListener(this, document.body, `keyup`, this.helpers.shiftKeyListener);
    }

    if (this.attr(`window-scroll`)) {
      useEventListener(this, window, `scroll`, this.helpers.handleScroll);
    }

    requestAnimationFrame(() => {
      if (!this.attr(`loading`)) {
        this.helpers.trackLoaded();
      }

      if (this.attr(`default-sort-field`) || this.attr(`default-sort-order`)) {
        this._emitSortChange(false);
      }
    });
  }

  public attributeChangedCallback(attr, oldVal, newVal) {
    super.attributeChangedCallback(attr, oldVal, newVal);
    if (attr === `search-filter`) {
      this.resetScroll();
    } else if (attr === `default-sort-field`) {
      // override state from attr if specified
      this.update({sortField: this.attr(attr)});
      this.resetScroll();
      this._emitSortChange(false);
    } else if (attr === `default-sort-order`) {
      this.update({sortOrder: this.attr(attr)});
      this.resetScroll();
      this._emitSortChange(false);
    } else if (attr === `loading`) {
      if (this.attr(attr)) {
        this.loadingStartTimeMs = Date.now();
      } else {
        this.helpers.trackLoaded();
      }
    }
  }

  public getDropPosition(ev: DragEvent) {
    const dropTarget = ev.currentTarget as HTMLElement;
    const rect = dropTarget.getBoundingClientRect();
    return ev.clientX - (rect.left + rect.width / 2) < 0 ? DROP_POSITION.LEFT : DROP_POSITION.RIGHT;
  }

  public resetScroll() {
    this.displayRowsCache = null;
    this.lastSelectedRowIdx = null;
    this.update({numRowsToDisplay: INITIAL_NUM_DISPLAY_ROWS});

    if (this.attr(`window-scroll`) && this.attr(`sticky-titles`)) {
      window.scrollTo(0, 0);
    } else if (this.elRefs.tableBody) {
      this.elRefs.tableBody.scrollTop = 0;
    }
  }

  public getDisplayedRows(): TableRow[] {
    return this._getDisplayRows().map((row) => row.val);
  }

  /** @param val - pass via prop for perf reasons */
  set rows(val: TableRow[]) {
    const numRowsToDisplay = this.state.numRowsToDisplay || INITIAL_NUM_DISPLAY_ROWS;
    this.update({
      rows: val,
      // clamp numRowsToDisplay between INITIAL_NUM_DISPLAY_ROWS and val.length
      numRowsToDisplay: Math.max(Math.min(val.length, numRowsToDisplay), INITIAL_NUM_DISPLAY_ROWS),
    });
    this.displayRowsCache = null;
  }

  set columns(val: TableColumn[]) {
    this.update({columns: val});
    this.displayRowsCache = null;
  }

  set rowDetailsRenderer(val: (row: TableRow) => VNode) {
    this.update({rowDetailsRenderer: val});
  }

  private _emitSortChange(isHeaderClick: boolean) {
    this.dispatchEvent(
      new CustomEvent(`sortChange`, {
        detail: {
          sortField: this.state.sortField,
          sortOrder: this.state.sortOrder,
          sortIndex: findIndex(this.state.columns, (col) => col.field === this.state.sortField),
          rows: this.getDisplayedRows(),
          isHeaderClick,
        },
      }) as SortChangeEvent,
    );
  }

  // This function moves the 'fromIndex' element just after the 'toIndex' element.
  private _moveColumnAfter(fromIndex: number, toIndex: number) {
    const columns = [...this.state.columns];
    const column = columns[fromIndex];
    columns.splice(fromIndex, 1);
    if (fromIndex < toIndex) {
      toIndex--;
    }
    columns.splice(toIndex + 1, 0, column);

    this.update({columns});
  }

  private _getCellLabel(row: TableRow, col: TableColumn) {
    const label = col.labelTransform ? col.labelTransform(row, col.field) : row[col.field];
    if (label === undefined || label === null) {
      return ``;
    }
    return label.toString();
  }

  // TODO opslead
  // eslint-disable-next-line sonarjs/cognitive-complexity
  private _getDisplayRows(): DisplayTableRow[] {
    // filter + sort is a significant overhead for tables with 1000s of rows
    // we memoize the display rows in a temporary cache so progressive scrolling is cheap
    if (this.displayRowsCache) {
      return this.displayRowsCache;
    }

    const {rows, columns, sortField, sortOrder} = this.state;

    const stickyRows = rows.slice(0, this.attr(`num-sticky-rows`));
    const displayStickyRows = stickyRows.map((val) => ({val, score: 0, matchParts: null}));

    const sortableRows = rows.slice(this.attr(`num-sticky-rows`));
    const searchFilter = this.attr(`search-filter`);
    const searchStr = searchFilter.trim().toLowerCase();
    let displaySortableRows = [];
    if (!searchStr) {
      // fast path if no searchStr
      displaySortableRows = sortableRows.map((val) => ({val, score: 0, matchParts: null}));
    } else {
      // iteration is faster than filter + map
      const searchableCols = columns.filter((col) => col.isSearchable);
      for (const val of sortableRows) {
        const matchParts = {};
        let score = 0;

        // try matching every searchable field
        for (const col of searchableCols) {
          const field = col.field;
          if (col.searchTransform) {
            const match = col.searchTransform(val, col.field, searchStr);
            if (match) {
              matchParts[field] = match.matchParts;
              score = Math.max(match.score, score);
            }
          } else {
            const targetStr = this._getCellLabel(val, col);
            const targetSanitizedStr = targetStr.toLowerCase();
            const match = fuzzyMatchSanitized(targetSanitizedStr, searchStr);

            if (match) {
              matchParts[field] = matchPartsFromSegments(targetStr, match.segments);
              score = Math.max(match.score, score);
            }
          }
        }

        if (score) {
          displaySortableRows.push({val, matchParts, score});
        }
      }
    }

    if (!this.attr(`disable-auto-sorting`) && (searchStr || sortField)) {
      const sortColumn = columns.find((col) => col.field === sortField);
      const sortTransform = sortColumn && sortColumn.sortTransform;

      // single sort function is faster than lexicalCompose
      // sorting is usually the n*log(n) bottleneck so we want it to be fast
      // prioritize match score, then sortTransform, then alphanumeric, then truthy
      displaySortableRows.sort((a, b) => {
        const scoreDiff = b.score - a.score;
        if (scoreDiff !== 0) {
          return scoreDiff;
        }

        if (sortTransform) {
          return sortOrder * (sortTransform(a.val, sortField) - sortTransform(b.val, sortField));
        }

        if (sortField) {
          const aVal = a.val[sortField];
          const bVal = b.val[sortField];
          const aType = typeof aVal;
          const bType = typeof bVal;

          if (aType === bType) {
            if (aType === `string`) {
              return sortOrder * aVal.trim().localeCompare(bVal.trim());
            } else if (aType === `number` || aType === `boolean`) {
              return sortOrder * (aVal - bVal);
            }
          }

          // truthy values come before falsey values
          return (aVal ? 0 : 1) - (bVal ? 0 : 1);
        }

        return 0;
      });
    }

    const displayRows = displayStickyRows.concat(displaySortableRows);
    if (displayRows.length !== this.numResults) {
      this.numResults = displayRows.length;
      this.dispatchEvent(
        new CustomEvent(`numResultsChange`, {detail: {numResults: this.numResults}}) as NumResultsChangeEvent,
      );
    }

    return (this.displayRowsCache = displayRows);
  }

  private _getSelectableDisplayedRows(): TableRow[] {
    return this.getDisplayedRows().filter((row) => !row.isSelectDisabled);
  }

  private _getDraggableAfterIndex(): number {
    return this.attr(`draggable-after-index`) || Number.NEGATIVE_INFINITY;
  }
}

export const TableEl = defineAndMakeJsxFactory<TableJsxProps>(COMPONENT_NAME, Table);
